/* eslint-disable import/no-anonymous-default-export */
const d3 = require('d3')
const moment = require('moment');
const { parseDate } = require('../utils/formatters.js');
const { wrap } = require( '../utils/shared-functions.js');
const { TRANSLATE_GENERAL_TAG } = require('../consts.js');

const PULSE_COLOR = '#4AA5D4';
const PULSE_BACKGROUND_COLOR = '#EBEFF3';
const EMPTY_COLOR = '#000000';
const MAX_PR_BPM = 240;
const MIN_PR_BPM = 20;
const margin = { top: 40, right: 30, bottom: 40, left: 50 };
const TRANSLATE_TAG = 'charts.pulse_rate'

exports.createPPGChart = function (rawData, thresholds, onClick, width, height, thresholdsStatus, translate) {

  const { 'Pulse rate': pulse_rate_threshold } = thresholds;
  const data = rawData.map((d) => {
      const date = parseDate(moment(d['date']).format('YYYY-MM-DD'));
      const empty = !d['avg'];
      const obj = {
        date,
        phigh: d['high'],
        plow: d['low'],
        popen: d['low'],
        pclose: d['high'],
        pavg: d['avg']?.toFixed(),
        empty,
      };
      return obj;
    });

    const x = d3.scaleBand()
    .domain(d3.utcDay.range(data[0].date, +data[data.length - 1].date + 1))
    .range([margin.left, width - margin.right])
    .padding(0.4);
  
  const y = d3.scaleLinear()
    .domain([MIN_PR_BPM, MAX_PR_BPM])
    .nice()
    .range([height - margin.bottom, margin.top]);
  
    const xAxis = (g) => 
      g.attr('transform', `translate(0,${height - margin.bottom - 2})`)
      .call(d3.axisBottom(x)
          .tickValues(d3.utcDay.every(1).range(data[0].date, +data[data.length - 1].date + 1))
          .tickFormat((d) => moment(d).format('ddd DD'))
          .tickSize(0)
      )
      .call((g) =>
        g.selectAll('.tick line')
          .clone()
          .attr('stroke-opacity', 0.2)
          .attr('transform', `translate(0,${-height + margin.top + margin.bottom + 3})`)
          .attr('y2', height - margin.top - margin.bottom)
      )
      .call((g) => g.select('.domain').remove());

  const yAxis = (g) =>
    g.attr('transform', `translate(${margin.left - 10},0)`)
    .call(d3.axisLeft(y)
          .tickValues([30, 40, 60, 80, 100, 120, 140, 160, 180, 200, 220, 240])
          .tickSize(0)
          .tickPadding(10)
      )
      .call((g) =>
        g.selectAll('.tick line')
        .clone()
        .attr('stroke-opacity', 0.2)
        .attr('x2', width - margin.left - margin.right)
      )
      .call((g) => g.select('.domain').remove());

  const pulseLineGenerate = d3
    .line()
    .defined((d) => !(d.popen === null && d.pclose === null))
    .x((d) => x(d.date))
    .y((d) => y(d.pavg));

  const svg = d3
    .create('svg')
    .attr('width', width)
    .attr('height', height);


    if(thresholdsStatus === 'on'){
      const pulseBackgroundColor = svg
        .append('rect')
        .attr('x', x(data[0].date))
        .attr('y', y(pulse_rate_threshold.high || 160))
        .attr('width', Math.abs(x(data[x.domain().length - 1].date) - x(data[0].date)) + x.bandwidth())
        .attr('height', Math.abs(y(pulse_rate_threshold.high || 160) - y(pulse_rate_threshold.low || 100)))
        .attr('fill', PULSE_BACKGROUND_COLOR)
        .attr('opacity', 1);  
    }

  const pulseLine = svg
    .append('path')
    .attr('d', pulseLineGenerate(data))
    .attr('fill', 'none')
    .attr('transform', (d) => `translate(10,0)`)
    .attr('stroke', 'black');

  svg.append('g').call(xAxis).selectAll('.tick text').call(wrap, x.bandwidth());
  svg.append('g').call(yAxis);

  const yAxisLabelDate = svg
  .append('text')
  .attr('x', margin.left - 40)
  .attr('y', margin.top - 20)
  .style('font-size', '12px')
  .attr('font-weight', '100')
  .text(translate ? translate('charts.units.bpm') : 'bpm');

  const xAxisLabelDate = svg
    .append('text')
    .attr('x', 20)
    .attr('y', height - margin.bottom + 20)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.general_words.date`) : 'Date');

  const xAxisLabelDay = svg
    .append('text')
    .attr('x', 20)
    .attr('y', height - margin.bottom + 33)
    .style('font-size', '12px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.general_words.date`) : 'Day');

  const fixLabelXPosition = width / data.length / 3.7
  const g = svg
    .append('g')
    .selectAll('g')
    .data(data)
    .join('g')
    .attr('transform', (d) => `translate(${x(d.date) + fixLabelXPosition},0)`);

  const emptyGroup = g.append('g').attr('visibility', (d) => d.empty ? 'visible' : 'hidden');
  const emptyBar = emptyGroup
    .append('line')
    .attr('y1', (d) => y(MIN_PR_BPM))
    .attr('y2', (d) => y(MAX_PR_BPM))
    .attr('stroke-width', 35)
    .attr('stroke', (d) => EMPTY_COLOR)
    .attr('opacity', 0.07);

  const pulseCandleGroup = g.append('g')
  .attr('visibility', (d) => !(d.popen === null && d.popen === null) ? 'visible' : 'hidden');
  
  const pulseCandle = pulseCandleGroup
    .append('line')
    .attr('y1', (d) => y(d.popen))
    .attr('y2', (d) => y(d.pclose))
    .attr('stroke-width', 12)
    .attr('stroke', PULSE_COLOR);

  const pulseCircle = pulseCandleGroup
    .append('circle')
    .attr('r', 10)
    .attr('cy', (d) => y(d.pavg))
    .attr('stroke-width', 1)
    .attr('fill', 'white')
    .attr('stroke', PULSE_COLOR)
    .style("cursor", "pointer")
    .on("click", (e, d) => onClick(d.date));

  const pulseCircleText = pulseCandleGroup
    .append('text')
    .filter((d)=>d.pavg)
    .attr('text-anchor', 'middle')
    .attr('dy', (d) => y(d.pavg) + 5)
    .style('font-size', '11px')
    .attr('font-weight', '400')
    .attr('font-family', 'roboto')
    .text((d) => d.pavg)
    .style("cursor", "pointer")
    .on("click", (e, d) => onClick(d.date));

  const gLegend = svg.append('g').attr('transform', (d) => `translate(${width-650},${margin.top - 15})`);
  const pulseLegendRect = gLegend
    .append('rect')
    .attr('x', 490)
    .attr('y', 0)
    .attr('width', 10)
    .attr('height', 10)
    .attr('fill', PULSE_COLOR);

  const pulseLegendText = gLegend
    .append('text')
    .attr('x', 505)
    .attr('y', 9)
    .style('font-size', '17px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_TAG}.title`) : 'Pulse rate');
  const pulseLegendAverageCircle = gLegend
    .append('circle')
    .attr('cx', 325)
    .attr('cy', 5)
    .attr('r', 7)
    .attr('stroke-width', 1)
    .attr('fill', 'white')
    .attr('stroke', '#4AA5D4');

  const pulseLegendAverageSign = gLegend
    .append('text')
    .attr('x', 321)
    .attr('y', 9)
    .style('font-size', '13px')
    .attr('font-weight', '100')
    .text('#');

  const pulseLegendAverageText = gLegend
    .append('text')
    .attr('x', 335)
    .attr('y', 9)
    .style('font-size', '17px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_TAG}.avg`) : 'Average Pulse rate');

  const missingValuesLegendRect = gLegend
    .append('rect')
    .attr('x', 150)
    .attr('y', 0)
    .attr('width', 10)
    .attr('height', 10)
    .attr('opacity', 0.11)
    .attr('fill', '#000000');

  const missingValuesLegendText = gLegend
    .append('text')
    .attr('x', 165)
    .attr('y', 9)
    .style('font-size', '17px')
    .attr('font-weight', '100')
    .text(translate ? translate(`${TRANSLATE_GENERAL_TAG}.legends.no_measure`) : 'No measurements');

  return svg.node();
}

